import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context/auth";
import { useCurrency } from "../../utils";
import {
  getPlanCobros,
  crearPlanCobros,
  eliminarPlanCobros,
  getPlanPagos,
  crearPlanPagos,
  eliminarPlanPagos,
} from "../../utils/planCobros";
import { client } from "../../utils/supabase";

const PlanCuotasModal = ({ isOpen, onClose, tipo, factura, onSuccess }) => {
  const { id: userId, pais } = useAuthContext();
  const { formatAmount } = useCurrency();
  const [cuotas, setCuotas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [numCuotas, setNumCuotas] = useState(2);
  const [modo, setModo] = useState("unico");
  const [fechaPagoUnico, setFechaPagoUnico] = useState("");

  const esCobro = tipo === "cobro";
  const monto = Number(factura?.monto_total || 0);
  const moneda = factura?.moneda || "CLP";
  const fmt = (v) => formatAmount(v, moneda);

  const titulo = esCobro ? `Cobro — Folio ${factura?.folio}` : `Pago — Folio ${factura?.folio}`;

  useEffect(() => {
    if (!isOpen || !factura) return;
    cargarCuotas();
    // Setear fecha inicial
    const fechaInicial = esCobro
      ? factura.facturafechacobro || factura.plazocobro || ""
      : factura.plazopago || factura.fecha_display || "";
    setFechaPagoUnico(fechaInicial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, factura]);

  const cargarCuotas = async () => {
    setLoading(true);
    try {
      const data = esCobro ? await getPlanCobros(factura.id) : await getPlanPagos(factura.id);
      setCuotas(data);
      setModo(data.length > 0 ? "cuotas" : "unico");
    } catch {
      toast.error("Error cargando plan");
    } finally {
      setLoading(false);
    }
  };

  const generarCuotas = () => {
    if (numCuotas < 2 || numCuotas > 12) return toast.error("Entre 2 y 12 cuotas");
    const montoPorCuota = Math.floor(monto / numCuotas);
    const diferencia = monto - montoPorCuota * numCuotas;
    const hoy = new Date();
    const nuevas = Array.from({ length: numCuotas }, (_, i) => {
      const fecha = new Date(hoy);
      fecha.setMonth(fecha.getMonth() + i + 1);
      return {
        numero_cuota: i + 1,
        monto_cuota: i === numCuotas - 1 ? montoPorCuota + diferencia : montoPorCuota,
        fecha_vencimiento: fecha.toISOString().split("T")[0],
        estado: "pendiente",
      };
    });
    setCuotas(nuevas);
  };

  const handleCambiarCuota = (idx, field, value) => {
    setCuotas((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  const handleGuardar = async () => {
    if (!factura) return;
    setLoading(true);
    try {
      if (modo === "unico") {
        if (!fechaPagoUnico) return toast.error("Ingresá una fecha");
        // Actualizar fecha directamente en la factura
        await client
          .from(esCobro ? "facturas_sii_emitidas" : "facturas_sii_recibidas")
          .update({ [esCobro ? "facturafechacobro" : "plazopago"]: fechaPagoUnico })
          .eq("id", factura.id);
        // Eliminar plan de cuotas si existía
        await (esCobro ? eliminarPlanCobros(factura.id) : eliminarPlanPagos(factura.id));
        toast.success("Fecha actualizada");
      } else {
        if (cuotas.length === 0) return toast.error("Generá al menos una cuota");
        const totalCuotas = cuotas.reduce((s, c) => s + Number(c.monto_cuota), 0);
        if (Math.abs(totalCuotas - monto) > 1) {
          toast.error(`Total cuotas ${fmt(totalCuotas)} ≠ monto factura ${fmt(monto)}`);
          return;
        }
        await (esCobro ? eliminarPlanCobros(factura.id) : eliminarPlanPagos(factura.id));
        const payload = cuotas.map((c, i) => ({
          pais,
          ...(esCobro ? { factura_sii_emitida_id: factura.id } : { factura_sii_recibida_id: factura.id }),
          numero_cuota: i + 1,
          monto_cuota: Number(c.monto_cuota),
          fecha_vencimiento: c.fecha_vencimiento,
          estado: c.estado || "pendiente",
          created_by: userId,
        }));
        await (esCobro ? crearPlanCobros(payload) : crearPlanPagos(payload));
        toast.success("Plan guardado");
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error("Error al guardar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  if (!factura) return null;

  const totalCuotas = cuotas.reduce((s, c) => s + Number(c.monto_cuota || 0), 0);
  const diferencia = monto - totalCuotas;

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>{titulo}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Info factura */}
        <div
          style={{
            background: "#f8f9fa",
            borderRadius: 6,
            padding: "10px 14px",
            marginBottom: 16,
            fontSize: "0.85rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <strong>{esCobro ? factura.razon_social_receptor : factura.razon_social_emisor}</strong>
          <span style={{ color: esCobro ? "#27ae60" : "#c0392b", fontWeight: 700 }}>{fmt(monto)}</span>
        </div>

        {/* Toggle modo */}
        <div
          style={{
            display: "flex",
            gap: 0,
            marginBottom: 16,
            border: "1px solid #dee2e6",
            borderRadius: 6,
            overflow: "hidden",
            width: "fit-content",
          }}
        >
          {[
            { key: "unico", label: "Pago único" },
            { key: "cuotas", label: "En cuotas" },
          ].map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setModo(m.key)}
              style={{
                padding: "6px 20px",
                fontSize: "0.82rem",
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
                background: modo === m.key ? "#2c3e50" : "transparent",
                color: modo === m.key ? "#fff" : "#666",
              }}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* Pago único */}
        {modo === "unico" && (
          <div>
            <label style={{ fontSize: "0.82rem", fontWeight: 600, display: "block", marginBottom: 6 }}>
              {esCobro ? "Fecha de cobro" : "Fecha de pago"}
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={fechaPagoUnico}
              onChange={(e) => setFechaPagoUnico(e.target.value)}
              style={{ maxWidth: 200 }}
            />
          </div>
        )}

        {/* Cuotas */}
        {modo === "cuotas" && (
          <>
            {cuotas.length === 0 && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
                <span style={{ fontSize: "0.85rem" }}>Generar</span>
                <input
                  type="number"
                  min={2}
                  max={12}
                  value={numCuotas}
                  onChange={(e) => setNumCuotas(parseInt(e.target.value))}
                  className="form-control form-control-sm"
                  style={{ width: 60 }}
                />
                <span style={{ fontSize: "0.85rem" }}>cuotas iguales</span>
                <button className="btn btn-sm btn-primary" onClick={generarCuotas}>
                  Generar
                </button>
              </div>
            )}

            {cuotas.length > 0 && (
              <>
                <table className="table table-sm table-bordered" style={{ fontSize: "0.83rem" }}>
                  <thead className="table-dark">
                    <tr>
                      <th>N°</th>
                      <th>Fecha vencimiento</th>
                      <th style={{ textAlign: "right" }}>Monto</th>
                      <th style={{ textAlign: "center" }}>Estado</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {cuotas.map((c, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={c.fecha_vencimiento || ""}
                            onChange={(e) => handleCambiarCuota(i, "fecha_vencimiento", e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={c.monto_cuota || ""}
                            onChange={(e) => handleCambiarCuota(i, "monto_cuota", e.target.value)}
                            style={{ textAlign: "right" }}
                          />
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span
                            style={{
                              fontSize: "0.7rem",
                              padding: "2px 6px",
                              borderRadius: 3,
                              background: c.estado === "cobrado" || c.estado === "pagado" ? "#d4edda" : "#fff3cd",
                              color: c.estado === "cobrado" || c.estado === "pagado" ? "#155724" : "#856404",
                            }}
                          >
                            {c.estado || "Pendiente"}
                          </span>
                        </td>
                        <td>
                          <button
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b" }}
                            onClick={() => setCuotas((prev) => prev.filter((_, idx) => idx !== i))}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div style={{ textAlign: "right", fontSize: "0.83rem", marginTop: 8 }}>
                  <span>
                    Total: <strong>{fmt(totalCuotas)}</strong>
                  </span>
                  {Math.abs(diferencia) > 0 && (
                    <span style={{ marginLeft: 12, color: "#c0392b" }}>Diferencia: {fmt(diferencia)}</span>
                  )}
                </div>

                <button
                  className="btn btn-sm btn-outline-secondary"
                  style={{ marginTop: 8 }}
                  onClick={() => setCuotas([])}
                >
                  Reiniciar
                </button>
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={loading}>
          {loading ? "Guardando..." : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PlanCuotasModal;
